import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Leaf,
  User,
  Mail,
  Phone,
  MapPin,
  Loader2,
  CheckCircle,
  Building2,
  Scissors,
  Sparkles,
  Award,
  TrendingUp,
  Users,
  BookOpen,
  Heart,
  ArrowRight,
  Shield,
  Gift,
  Star,
  Zap,
  Target,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = window.location.origin;

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_3aa397d2-df3c-4e29-af8c-edf4212ba1cc/artifacts/lj4kelec_image.png";

const BENEFITS = [
  {
    icon: BookOpen,
    title: "Formazione Gratuita",
    description: "Accedi a corsi e articoli scientifici su benessere, trattamenti e nuove tendenze del settore.",
    color: "bg-rose-100 text-rose-600"
  },
  {
    icon: TrendingUp,
    title: "Crescita Professionale",
    description: "Contenuti per distinguerti dalla concorrenza e offrire servizi di valore ai tuoi clienti.",
    color: "bg-purple-100 text-purple-600"
  },
  {
    icon: Users,
    title: "Network di Professionisti",
    description: "Connettiti con altri professionisti del settore beauty e wellness in Italia.",
    color: "bg-indigo-100 text-indigo-600"
  },
  {
    icon: Gift,
    title: "Tutto Gratuito",
    description: "Nessun costo, nessun vincolo, nessun abbonamento. Cancellati quando vuoi.",
    color: "bg-amber-100 text-amber-600"
  }
];

const TESTIMONIALS = [
  {
    name: "Maria R.",
    role: "Centro Estetico Aurora, Milano",
    text: "Grazie ai contenuti formativi ho potuto offrire nuovi servizi ai miei clienti. Consigliatissimo!"
  },
  {
    name: "Giuseppe M.",
    role: "Hair Stylist, Roma",
    text: "Finalmente una piattaforma che capisce le esigenze dei professionisti del settore beauty."
  }
];

export default function ProBenessereClubPage() {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [gdprConsent, setGdprConsent] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: "",
    cognome: "",
    email: "",
    cellulare: "",
    citta: "",
    regione: "",
    tipologia: "",
    nome_attivita: "",
    note: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.nome || !formData.cognome || !formData.email || !formData.tipologia) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    
    if (!gdprConsent) {
      toast.error("Devi accettare l'informativa sulla privacy");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/public/register-partner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          fonte: "pro_benessere_club"
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
        toast.success("Richiesta inviata con successo!");
      } else {
        toast.error(data.detail || "Errore nell'invio della richiesta");
      }
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Errore di connessione");
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center shadow-xl">
          <CardContent className="pt-10 pb-8">
            <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Richiesta Ricevuta!</h2>
            <p className="text-slate-600 mb-6">
              Grazie per il tuo interesse! Un nostro consulente ti contatterà entro 24-48 ore 
              per illustrarti tutti i vantaggi della community.
            </p>
            <div className="space-y-3">
              <Link to="/benessere">
                <Button className="w-full bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700">
                  Esplora i Contenuti
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 via-white to-pink-50" data-testid="pro-benessere-club-page">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <span className="font-serif text-lg font-semibold text-slate-800">ODR Academy</span>
            </Link>
            <Link to="/benessere">
              <Button variant="ghost" size="sm">
                Scopri i Contenuti
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-600/10 to-pink-600/10" />
        <div className="max-w-7xl mx-auto px-4 py-16 sm:py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <div className="text-center lg:text-left">
              <Badge className="bg-rose-100 text-rose-700 mb-4 px-4 py-1">
                <Award className="w-3 h-3 mr-1" />
                Per Professionisti del Beauty
              </Badge>
              <h1 className="font-serif text-4xl sm:text-5xl font-bold text-slate-900 mb-6 leading-tight">
                Fai Crescere il Tuo{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-600 to-pink-600">
                  Business
                </span>{" "}
                nel Benessere
              </h1>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Sei un'<strong>estetista</strong>, <strong>parrucchiere</strong> o gestisci un <strong>centro benessere</strong>? 
                Unisciti alla nostra community esclusiva e accedi gratuitamente a formazione, 
                contenuti e strumenti per far crescere la tua attività.
              </p>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-8">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Shield className="w-4 h-4 text-green-500" />
                  <span>Zero Vincoli</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Gift className="w-4 h-4 text-green-500" />
                  <span>100% Gratuito</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Zap className="w-4 h-4 text-amber-500" />
                  <span>Supporto Dedicato</span>
                </div>
              </div>

              {/* Activity Types */}
              <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                <Badge variant="outline" className="bg-white/80">
                  <Sparkles className="w-3 h-3 mr-1 text-rose-500" />
                  Centri Estetici
                </Badge>
                <Badge variant="outline" className="bg-white/80">
                  <Scissors className="w-3 h-3 mr-1 text-amber-500" />
                  Parrucchieri
                </Badge>
                <Badge variant="outline" className="bg-white/80">
                  <Leaf className="w-3 h-3 mr-1 text-teal-500" />
                  SPA & Wellness
                </Badge>
                <Badge variant="outline" className="bg-white/80">
                  <Heart className="w-3 h-3 mr-1 text-pink-500" />
                  Beauty Specialist
                </Badge>
              </div>
            </div>

            {/* Right - Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={HERO_IMAGE}
                  alt="Professionisti del benessere"
                  className="w-full h-80 sm:h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              {/* Floating Card */}
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-4 hidden sm:block">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center">
                    <Users className="w-6 h-6 text-rose-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-800">1000+</p>
                    <p className="text-sm text-slate-500">Professionisti Iscritti</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl font-bold text-slate-900 mb-4">
              Perché Unirti a Noi
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Tutto completamente gratuito. La piattaforma è in continuo sviluppo 
              con nuove funzionalità dedicate ai professionisti del settore.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {BENEFITS.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className={`w-14 h-14 mx-auto rounded-xl ${benefit.color} flex items-center justify-center mb-4`}>
                    <benefit.icon className="w-7 h-7" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-slate-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-12 bg-gradient-to-r from-rose-500 to-pink-600">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <Card key={i} className="bg-white/10 backdrop-blur border-white/20">
                <CardContent className="p-6 text-white">
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="mb-4 text-white/90 italic">"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <User className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="font-semibold">{t.name}</p>
                      <p className="text-sm text-white/70">{t.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form Section */}
      <section className="py-16 bg-gradient-to-b from-white to-rose-50" id="registrati">
        <div className="max-w-xl mx-auto px-4">
          <Card className="shadow-2xl border-0 overflow-hidden">
            <div className="bg-gradient-to-r from-rose-500 to-pink-600 p-6 text-white text-center">
              <Building2 className="w-12 h-12 mx-auto mb-3 opacity-90" />
              <h2 className="text-2xl font-bold mb-1">Richiedi Accesso</h2>
              <p className="text-rose-100 text-sm">Ti contatteremo entro 24-48 ore</p>
            </div>
            
            <CardContent className="p-6 sm:p-8">
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Tipologia */}
                <div>
                  <Label>Tipo di Attività *</Label>
                  <Select onValueChange={(value) => handleSelectChange("tipologia", value)}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Seleziona la tua attività" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="centro_estetico">Centro Estetico</SelectItem>
                      <SelectItem value="parrucchiere">Parrucchiere / Hair Stylist</SelectItem>
                      <SelectItem value="spa_wellness">SPA / Centro Benessere</SelectItem>
                      <SelectItem value="farmacia">Farmacia / Parafarmacia</SelectItem>
                      <SelectItem value="freelance">Professionista Freelance</SelectItem>
                      <SelectItem value="altro">Altro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Nome Attività */}
                <div>
                  <Label htmlFor="nome_attivita">Nome Attività</Label>
                  <div className="relative mt-1">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="nome_attivita"
                      name="nome_attivita"
                      value={formData.nome_attivita}
                      onChange={handleChange}
                      placeholder="Es. Centro Estetico Aurora"
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Nome e Cognome */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nome">Nome *</Label>
                    <Input
                      id="nome"
                      name="nome"
                      value={formData.nome}
                      onChange={handleChange}
                      placeholder="Mario"
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="cognome">Cognome *</Label>
                    <Input
                      id="cognome"
                      name="cognome"
                      value={formData.cognome}
                      onChange={handleChange}
                      placeholder="Rossi"
                      className="mt-1"
                      required
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="info@tuocentro.it"
                      className="pl-10"
                      required
                    />
                  </div>
                </div>

                {/* Telefono */}
                <div>
                  <Label htmlFor="cellulare">Telefono</Label>
                  <div className="relative mt-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="cellulare"
                      name="cellulare"
                      type="tel"
                      value={formData.cellulare}
                      onChange={handleChange}
                      placeholder="+39 333 1234567"
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Città e Regione */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="citta">Città</Label>
                    <Input
                      id="citta"
                      name="citta"
                      value={formData.citta}
                      onChange={handleChange}
                      placeholder="Milano"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Regione</Label>
                    <Select onValueChange={(value) => handleSelectChange("regione", value)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Seleziona" />
                      </SelectTrigger>
                      <SelectContent>
                        {["Lombardia", "Lazio", "Campania", "Sicilia", "Veneto", "Emilia-Romagna", "Piemonte", "Puglia", "Toscana", "Calabria", "Sardegna", "Liguria", "Marche", "Abruzzo", "Friuli-Venezia Giulia", "Trentino-Alto Adige", "Umbria", "Basilicata", "Molise", "Valle d'Aosta"].map((reg) => (
                          <SelectItem key={reg} value={reg}>{reg}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Note */}
                <div>
                  <Label htmlFor="note">Messaggio (opzionale)</Label>
                  <Textarea
                    id="note"
                    name="note"
                    value={formData.note}
                    onChange={handleChange}
                    placeholder="Raccontaci della tua attività..."
                    className="mt-1 min-h-[80px]"
                  />
                </div>

                {/* GDPR */}
                <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                  <Checkbox
                    id="gdpr"
                    checked={gdprConsent}
                    onCheckedChange={setGdprConsent}
                    className="mt-0.5"
                  />
                  <Label htmlFor="gdpr" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
                    Accetto l'<Link to="/privacy" className="text-rose-600 hover:underline">informativa privacy</Link> e 
                    acconsento ad essere contattato per informazioni sui servizi.
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white font-semibold rounded-xl shadow-lg shadow-rose-500/25"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      Richiedi Accesso Gratuito
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Bottom Note */}
          <div className="text-center mt-8 text-sm text-slate-500">
            <p className="flex items-center justify-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              Piattaforma in sviluppo - Nuove funzionalità per professionisti in arrivo
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-slate-400 text-sm mb-2">
            © 2025 ODR Academy - La Scienza del Benessere
          </p>
          <p className="text-slate-500 text-xs">
            Tutti i diritti riservati
          </p>
        </div>
      </footer>
    </div>
  );
}
